import { SPLIT, gbp0, gbp2, pct, pctTone, varTone, shortDate } from "./reportFormat";

const th = "px-2 py-1.5 text-right font-semibold text-[11px] text-slate-600 whitespace-nowrap";
const td = "px-2 py-1 text-right font-mono tabular-nums whitespace-nowrap";

// Weekly + Monthly blocks (the sheet's S..AB columns) and the month-model target row.
export function ReportSideTables({ data }) {
  const weeks = data?.weeks || [];
  const m = data?.monthly || {};
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]" data-testid="report-side-tables">
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <div className="px-3 py-1.5 bg-sky-50 text-sky-900 text-[11px] font-bold uppercase tracking-wide">Weekly Report</div>
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 border-b border-slate-200"><th className={`${th} text-left`}>Week</th><th className={th}>Loan Due</th><th className={th}>Cash Collected</th><th className={th}>Collection %</th><th className={th}>Variance</th></tr></thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.n} className="border-b border-slate-100" data-testid={`week-row-${w.n}`}>
                <td className="px-2 py-1 whitespace-nowrap">Week {w.n} <span className="text-slate-400">· {shortDate(w.from).slice(4)} – {shortDate(w.to).slice(4)}</span></td>
                <td className={td}>{w.days ? gbp2(w.loan_due) : "—"}</td>
                <td className={td}>{w.days ? gbp2(w.cash_collected) : "—"}</td>
                <td className={`${td} font-semibold ${w.days ? pctTone(w.collection_pct) : "text-slate-300"}`}>{w.days ? pct(w.collection_pct) : "—"}</td>
                <td className={`${td} ${w.days ? varTone(w.variance) : "text-slate-300"}`}>{w.days ? gbp2(w.variance) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 text-[11px] text-slate-600" data-testid="weekly-averages">
              <td className="px-2 py-1.5 font-semibold">Weekly average</td>
              <td className={td} title="Average target">{gbp2(m.avg_weekly_loan_due)}</td>
              <td className={td} title="Average collection">{gbp2(m.avg_weekly_cash)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="px-3 py-1.5 bg-violet-50 text-violet-900 text-[11px] font-bold uppercase tracking-wide">Monthly Report</div>
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 border-b border-slate-200"><th className={`${th} text-left`} /><th className={th}>Loan Due</th><th className={th}>Cash</th><th className={th}>%</th><th className={th}>Variance</th></tr></thead>
          <tbody>
            <tr className="border-b border-slate-100" data-testid="monthly-actual">
              <td className="px-2 py-1.5 font-medium">Month to date</td>
              <td className={td}>{gbp0(m.loan_due)}</td><td className={td}>{gbp0(m.cash_collected)}</td>
              <td className={`${td} font-semibold ${pctTone(m.collection_pct)}`}>{pct(m.collection_pct, 1)}</td>
              <td className={`${td} ${varTone(m.variance)}`}>{gbp0(m.variance)}</td>
            </tr>
            <tr data-testid="monthly-model">
              <td className="px-2 py-1.5 font-medium" title={`Receivables on day 1 × ${((m.model?.rate || 0) * 100).toFixed(0)}% ÷ ${m.model?.avg_working_days} × ${m.working_days} working days`}>Model target</td>
              <td className={td}>{gbp0(m.model_target)}</td><td className={td}>{gbp0(m.cash_collected)}</td>
              <td className={`${td} font-semibold ${m.model_target ? pctTone(m.model_pct) : "text-slate-300"}`}>{m.model_target ? pct(m.model_pct, 1) : "—"}</td>
              <td className={`${td} ${m.model_target ? varTone(m.model_variance) : "text-slate-300"}`}>{m.model_target ? gbp0(m.model_variance) : "—"}</td>
            </tr>
          </tbody>
        </table>
        <p className="px-3 pb-2 pt-1 text-[10px] text-slate-400">{m.days || 0} days with figures · {m.working_days || 0} working days · open loans {m.open_loans_change > 0 ? "+" : ""}{m.open_loans_change ?? 0} this month{m.upcoming_loan_due ? ` · ${gbp0(m.upcoming_loan_due)} due on upcoming days` : ""}</p>
        {data?.region === "AUS" && (
          <div className="border-t border-slate-100 px-3 py-2 text-[11px]" data-testid="monthly-split">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100" title="Share of the month's cash by channel">
              {SPLIT.map((s) => { const v = Number(m[s.key] || 0); const tot = Number(m.cash_collected || 0); return tot > 0 && v > 0 ? <span key={s.key} style={{ width: `${(v / tot) * 100}%`, background: s.color }} /> : null; })}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {SPLIT.map((s) => <span key={s.key} className="inline-flex items-center gap-1 text-slate-600"><span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />{s.label} <span className="font-mono tabular-nums">{gbp0(m[s.key])}</span></span>)}
              {m.gc_failed_count > 0 && <span className="text-rose-700" data-testid="monthly-failed">{m.gc_failed_count} failed · {gbp0(m.gc_failed_value)}</span>}
              {m.sheet_days > 0 && <span className="text-stone-500" data-testid="monthly-sheet-days">{m.sheet_days} day{m.sheet_days === 1 ? "" : "s"} from the sheet</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
